import {useState} from "react";
import {
    ArrowLeft,
    Building2,
    Clock,
    Inbox,
    Mail,
    MessageSquare,
    Phone,
    Search,
    Tag,
    User,
    Briefcase,
    Globe,
    LucideIcon,
} from "lucide-react";
import {Card} from "./ui/Card";
import {SearchInput} from "./ui/SearchInput";
import {EmptyState} from "./ui/EmptyState";
import {staggerDelay} from "../utils/animation";

interface Submission {
    data: Record<string, any>;

    [key: string]: any;
}

interface SubmissionsProps {
    formName: string;
    submissions: Submission[] | undefined;
    onBack: () => void;
}

const FIELD_ICONS: Record<string, LucideIcon> = {
    name: User,
    fullname: User,
    firstname: User,
    lastname: User,
    email: Mail,
    company: Building2,
    role: Briefcase,
    jobtitle: Briefcase,
    phone: Phone,
    phonenumber: Phone,
    message: MessageSquare,
    subject: MessageSquare,
    comments: MessageSquare,
    website: Globe,
};

const LONG_TEXT_KEYS = new Set(["message", "subject", "comments", "description", "notes"]);
const QA_QUESTION_KEYS = new Set(["question", "text", "prompt", "label"]);
const QA_ANSWER_KEYS = new Set(["response", "answer"]);
const QA_SKIP_KEYS = new Set(["questionid", "id"]);

const normalizeKey = (key: string) => key.toLowerCase().replace(/[^a-z]/g, "");

const formatLabel = (key: string) => key.replace(/[_-]+/g, " ").trim();

const getFieldIcon = (key: string): LucideIcon => FIELD_ICONS[normalizeKey(key)] ?? Tag;

const isLongTextField = (key: string, value: unknown) =>
    LONG_TEXT_KEYS.has(normalizeKey(key)) || (typeof value === "string" && value.length > 60);

const getInitials = (name: string) =>
    name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0]?.toUpperCase())
        .join("");

const renderArrayField = (value: any[]) => (
    <div className="space-y-2">
        {value.map((item, j) => {
            if (typeof item !== "object" || item === null) {
                return (
                    <div key={j} className="text-sm text-ink-muted rounded-lg bg-surface-sunken px-3.5 py-2.5">
                        {String(item)}
                    </div>
                );
            }

            const itemEntries = Object.entries(item);
            const questionEntry = itemEntries.find(([k]) => QA_QUESTION_KEYS.has(normalizeKey(k)));
            const answerEntry = itemEntries.find(([k]) => QA_ANSWER_KEYS.has(normalizeKey(k)));

            if (questionEntry || answerEntry) {
                const restEntries = itemEntries.filter(([k]) =>
                    k !== questionEntry?.[0] && k !== answerEntry?.[0] && !QA_SKIP_KEYS.has(normalizeKey(k))
                );

                return (
                    <div key={j} className="rounded-xl border border-border bg-surface-sunken p-3.5">
                        <div className="flex items-start gap-3">
                            <span className="shrink-0 mt-0.5 size-5 rounded-full bg-primary-tint text-primary text-[10px] font-bold flex items-center justify-center">
                                {j + 1}
                            </span>
                            <div className="min-w-0 flex-1 space-y-2">
                                {questionEntry && (
                                    <p className="text-sm font-semibold text-ink">{String(questionEntry[1])}</p>
                                )}
                                {answerEntry && (
                                    <p className="text-sm text-ink-muted border-l-2 border-primary/50 pl-3">
                                        {String(answerEntry[1])}
                                    </p>
                                )}
                                {restEntries.length > 0 && (
                                    <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
                                        {restEntries.map(([subKey, subValue]) => (
                                            <span key={subKey} className="text-xs text-ink-faint">
                                                {formatLabel(subKey)}: <span className="text-ink-muted">{String(subValue)}</span>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            }

            return (
                <div key={j} className="rounded-xl border border-border bg-surface-sunken p-3.5 space-y-1.5">
                    {itemEntries.map(([subKey, subValue]) => (
                        <div key={subKey} className="text-sm">
                            <span className="text-ink-faint font-semibold">{formatLabel(subKey)}:</span>{' '}
                            <span className="text-ink-muted">{String(subValue)}</span>
                        </div>
                    ))}
                </div>
            );
        })}
    </div>
);

export const Submissions = ({submissions, formName, onBack}: SubmissionsProps) => {
    const [searchTerm, setSearchTerm] = useState("");
    const filteredSubmissions = submissions?.filter((sub) => {
        const search = searchTerm.toLowerCase();
        const inData = Object.entries(sub.data ?? {}).some(([key, value]) =>
            String(key).toLowerCase().includes(search) ||
            String(value).toLowerCase().includes(search)
        );
        const inRoot = Object.entries(sub)
            .filter(([key]) => key !== "data")
            .some(([key, value]) =>
                String(key).toLowerCase().includes(search) ||
                String(value).toLowerCase().includes(search)
            );

        return inData || inRoot;
    });

    const totalCount = submissions?.length ?? 0;

    return (
        <div className="w-full h-full flex flex-col">
            <div className="flex gap-3 items-center mb-6 shrink-0">
                <button
                    onClick={onBack}
                    className="size-9 shrink-0 flex items-center justify-center rounded-full text-ink-muted hover:text-primary hover:bg-surface-hover transition-colors"
                >
                    <ArrowLeft size={18}/>
                </button>
                <div className="min-w-0">
                    <h1 className="text-2xl font-display font-bold text-ink truncate">
                        Submissions for {formName}
                    </h1>
                    <p className="text-sm text-ink-muted mt-1">
                        {totalCount} submission{totalCount === 1 ? "" : "s"} received.
                    </p>
                </div>
            </div>

            <div className="mb-5 shrink-0">
                <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Search submissions..."/>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-3">
                {filteredSubmissions === undefined ? (
                    <Card>
                        <EmptyState
                            icon={Inbox}
                            title="There are no submissions for this form."
                            description="Once someone fills it out, their responses will show up here."
                        />
                    </Card>
                ) : filteredSubmissions.length === 0 ? (
                    <Card>
                        <EmptyState
                            icon={Search}
                            title="No submissions match your search."
                            description="Try a different search term."
                        />
                    </Card>
                ) : (
                    filteredSubmissions.map((submission, index) => {
                        const rawDate = submission.metadata?.created_at
                            ? new Date(submission.metadata.created_at)
                            : submission._id
                                ? new Date(parseInt(String(submission._id).substring(0, 8), 16) * 1000)
                                : null;
                        const formattedDate = rawDate && !isNaN(rawDate.getTime())
                            ? rawDate.toLocaleString()
                            : "Unknown date";

                        const entries = Object.entries(submission.data ?? {});
                        const nameEntry = entries.find(([key]) => {
                            const normalized = normalizeKey(key);
                            return normalized === "name" || normalized === "fullname";
                        });
                        const displayName = typeof nameEntry?.[1] === "string" && nameEntry[1].trim()
                            ? nameEntry[1].trim()
                            : `Submission #${index + 1}`;

                        return (
                            <Card
                                key={index}
                                className="hover:border-border-strong transition-colors duration-150 animate-fade-in-up"
                                style={staggerDelay(index)}
                            >
                                <div className="flex items-center justify-between gap-4 mb-5 pb-4 border-b border-border">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="size-10 shrink-0 rounded-full bg-primary-tint text-primary flex items-center justify-center text-xs font-bold font-display">
                                            {nameEntry ? getInitials(displayName) || "#" : `#${index + 1}`}
                                        </div>
                                        <div className="min-w-0">
                                            <h3 className="text-sm font-display font-bold text-ink truncate">
                                                {displayName}
                                            </h3>
                                            <span className="text-xs text-ink-faint">Submission #{index + 1}</span>
                                        </div>
                                    </div>
                                    <span className="flex items-center gap-1.5 text-xs text-ink-muted shrink-0">
                                        <Clock size={12}/>
                                        {formattedDate}
                                    </span>
                                </div>

                                {entries.length === 0 ? (
                                    <p className="text-sm text-ink-faint">No data was submitted with this entry.</p>
                                ) : (
                                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                                        {entries.map(([key, value], i) => {
                                            const Icon = getFieldIcon(key);
                                            const isArrayValue = Array.isArray(value);
                                            const wide = isLongTextField(key, value) || isArrayValue;
                                            return (
                                                <div key={i} className={wide ? "sm:col-span-2" : undefined}>
                                                    <dt className="flex items-center justify-between gap-1.5 text-[11px] font-semibold tracking-wide text-ink-faint uppercase mb-1.5">
                                                        <span className="flex items-center gap-1.5">
                                                            <Icon size={12}/>
                                                            {formatLabel(key)}
                                                        </span>
                                                        {isArrayValue && value.length > 0 && (
                                                            <span className="text-ink-faint normal-case font-medium">
                                                                {value.length} item{value.length === 1 ? "" : "s"}
                                                            </span>
                                                        )}
                                                    </dt>
                                                    <dd className="text-sm">
                                                        {isArrayValue ? (
                                                            renderArrayField(value)
                                                        ) : typeof value === 'object' && value !== null ? (
                                                            <pre className="text-xs text-ink-muted bg-surface-sunken rounded-lg p-3 overflow-x-auto">
                                    {JSON.stringify(value, null, 2)}
                                </pre>
                                                        ) : isLongTextField(key, value) ? (
                                                            <p className="text-ink-muted bg-surface-sunken rounded-lg p-3 leading-relaxed whitespace-pre-wrap">
                                                                {String(value)}
                                                            </p>
                                                        ) : (
                                                            <span className="text-ink break-words">{String(value)}</span>
                                                        )}
                                                    </dd>
                                                </div>
                                            );
                                        })}
                                    </dl>
                                )}
                            </Card>
                        );
                    })
                )}
            </div>
        </div>
    );
};
